import type { Activity } from '@/features/tongue-lab/lessons';
import type { AgeBand } from '@/game/coach/phrases';

/**
 * Alex's Lab (docs/design/alex-lab.md): the reveal that Alex's r doesn't count (masks only answer
 * to a human r), the notebook pages that frame the 3D mouth's activities, the spoken stamps and
 * Alex's tries. Audio tags ([whispers]) shape the recording only (stripTags for captions).
 */

/** Alex's poses in the lab; until a pose's art exists, the nearest existing one stands in. */
export type LabPose = 'confide' | 'try' | 'sheepish' | 'present' | 'notebook' | 'hopeful';

export interface LabLine {
  text: string;
  pose: LabPose;
  /** From this line on, the scope is on and shows the 3D mouth. */
  scope?: boolean;
}

export const REVEAL: Readonly<Record<AgeBand, readonly LabLine[]>> = {
  younger: [
    {
      pose: 'confide',
      text: '[quietly] Agent, can I tell you a secret? A real one. Not a spy one.',
    },
    {
      pose: 'try',
      text: '[whispers] My rrr doesn’t work. Masks only listen to a person’s rrr. A bunny rrr just bounces off.',
    },
    {
      pose: 'sheepish',
      text: '[sheepish] I’ve tried and tried. My nose wiggles, my ears flop… and the masks don’t even blink.',
    },
    {
      pose: 'hopeful',
      text: '[warm] That’s why HQ needed you. You took the cat’s mask off. I never could.',
    },
    {
      pose: 'present',
      scope: true,
      text: '[proud] So I did the next best thing. I watched how people make it, with this. The Peek-Inside Scope! It sees right through cheeks.',
    },
    {
      pose: 'present',
      text: '[delighted] See the pink one in the middle? That’s the tongue. It does the rrr.',
    },
    {
      pose: 'notebook',
      text: '[earnest] I drew everything I saw in my notebook. But I need a person to check it. A person with a people tongue. Like you.',
    },
    { pose: 'hopeful', text: '[hopeful] Will you be my rrr checker? Say yes, or tap it.' },
  ],
  older: [
    {
      pose: 'confide',
      text: '[low, serious] Close the door, Agent. What I’m about to tell you isn’t in any file.',
    },
    {
      pose: 'sheepish',
      text: '[matter-of-fact] My r doesn’t count. Masks only answer to a human r. To them, mine is just noise. Twenty years at HQ, and I’ve never unmasked a single foe.',
    },
    {
      pose: 'confide',
      text: '[dry] Ever wondered why I stay behind the scanner? Now you know.',
    },
    {
      pose: 'present',
      scope: true,
      text: '[intense] So I did what spies do. I studied. Hundreds of scans, with this: the X-Scope. It looks straight through skin and bone.',
    },
    {
      pose: 'present',
      text: '[quietly proud] That’s a human mouth, live. For an r, the tongue does the work, out of sight. Nobody can copy what they can’t see.',
    },
    {
      pose: 'notebook',
      text: '[direct] My notes say where every part goes. But I’ve never felt a human r. You have.',
    },
    {
      pose: 'hopeful',
      text: '[wry] I need someone to test my notes, and tell me where I’m wrong. Deal?',
    },
  ],
};

/** After a no to the reveal's question. */
export const DECLINED: Readonly<Record<AgeBand, string>> = {
  younger: '[warm] That’s okay. My lab is always open. Just say “lab” in the elevator.',
  older: '[easy] No pressure. The lab’s in the elevator whenever you want it.',
};

/** What the gadget is called. */
export const SCOPE_NAME: Readonly<Record<AgeBand, string>> = {
  younger: 'Peek-Inside Scope',
  older: 'X-Scope',
};

export interface Note {
  activity: Activity;
  /** The notebook page's title, shown and stamped. */
  title: string;
  /** What gets stamped ("Stamped: tongue sides."), per age band. */
  checks: Readonly<Record<AgeBand, string>>;
  opening: Readonly<Record<AgeBand, string>>;
  /** The opening from the second round on, when the pages come back in any order. */
  again: Readonly<Record<AgeBand, string>>;
}

/** The notebook, in visit order (lessons.ts ORDER). */
export const NOTES: readonly Note[] = [
  {
    activity: 'watch',
    title: 'Watch the r',
    checks: { younger: 'the r, start to finish', older: 'the r, start to finish' },
    again: {
      younger:
        '[cheerful] My first drawing again! Watch it one more time. Does it still look right?',
      older: '[focused] Back to the basics: rest, then r. Watch it again with fresh eyes.',
    },
    opening: {
      younger: '[excited] My first drawing! Watch the tongue, and tell me if I drew it right.',
      older:
        '[focused] Note one: the resting tongue, and what changes for r. Watch it through, then we test it.',
    },
  },
  {
    activity: 'build',
    title: 'Build the r',
    checks: { younger: 'the five steps', older: 'the five steps' },
    again: {
      younger: '[cheerful] The rrr recipe again! You call the steps, I’ll move the tongue.',
      older: '[brisk] The recipe again. Five steps. Call them faster this time.',
    },
    opening: {
      younger: '[cheerful] Let’s build an rrr, one step at a time. You tell me each step.',
      older: '[brisk] Note two is a recipe. Five steps. You call each one; I’ll move the tongue.',
    },
  },
  {
    activity: 'quiz',
    title: 'Which is r?',
    checks: { younger: 'r and w, from the inside', older: 'r and w, from the inside' },
    again: {
      younger: '[curious] Rrr or w? Let’s peek inside again.',
      older: '[challenging] R or w, round two. Watch the tongue, not the lips.',
    },
    opening: {
      younger: '[curious] From outside, rrr and w look the same. From inside? Let’s peek and see.',
      older:
        '[challenging] From outside, r and w are almost twins. Inside, the tongue gives it away. Spot which is which.',
    },
  },
  {
    activity: 'fix',
    title: 'Spot the mistake',
    checks: { younger: 'the mixed-up pictures', older: 'the tampered scans' },
    again: {
      younger:
        '[worried] Oh no, my pictures got mixed up again! Can you find what’s wrong this time?',
      older: '[low] More tampered scans. Different mistakes this time. Find them.',
    },
    opening: {
      younger:
        '[worried] Uh-oh. Someone sneaked into my lab and mixed up my pictures! Can you find what’s wrong?',
      older:
        '[low] Someone’s been in my files. One of these scans has been tampered with. Find what’s wrong.',
    },
  },
  {
    activity: 'drag',
    title: 'Drive the tongue',
    checks: { younger: 'the tongue, moved by you', older: 'the tongue, moved by hand' },
    again: {
      younger: '[excited] You drive again! Push the tongue up into an rrr.',
      older: '[confident] Controls are yours again. Shape the r without the hints this time.',
    },
    opening: {
      younger: '[excited] Your turn to drive! Push the tongue up to make an rrr.',
      older:
        '[confident] You’re on the controls. Shape the r yourself; the scope will tell you how close you are.',
    },
  },
  {
    activity: 'spin',
    title: 'Every side',
    checks: { younger: 'the tongue’s sides', older: 'tongue sides' },
    again: {
      younger: '[curious] Spin it again! Can you still find the tongue’s sides?',
      older: '[intrigued] Another full rotation. Find the sides, then find the gap.',
    },
    opening: {
      younger: '[curious] Let’s look from every side. Find where the tongue’s sides go.',
      older: '[intrigued] Full rotation. The part everyone misses is on the sides. Find it.',
    },
  },
];

/** The stamp, spoken as two recorded lines: what was checked, then by whom. */
export function stampLines(note: Note, band: AgeBand, codename: string | null): [string, string] {
  return [`[official] Stamped: ${note.checks[band]}.`, byLine(codename)];
}

/** "Checked by Agent Red Rocket." (one recording per codename). */
export function byLine(codename: string | null): string {
  return codename ? `[warm] Checked by Agent ${codename}.` : '[warm] Checked by you.';
}

/** Alex's try at the end of a visit: one per visit, never on a mask, ending on what was checked. */
export const TRIES: Readonly<Record<AgeBand, readonly string[]>> = {
  younger: [
    '[determined] Okay. Middle up, sides on my teeth… [sniffs] …Nope. Still a bunny tongue.',
    '[focused] Middle up… [thump] Ooh, my foot did it instead. Silly foot.',
    '[bold] Strong, like a tiger… [squeak] …That was a very small tiger.',
    '[hopeful] Tip low, sides up… [ear flop] My ears are trying harder than my tongue.',
    '[giggles] I tried it in the mirror this morning. The mirror didn’t blink either.',
    '[hopeful] Here goes… [ear flop] …Just ears. My notebook works better than my mouth.',
  ],
  older: [
    '[determined] Right. Tongue up, sides braced… [ear flop] …Nothing. My notes are correct now. My mouth just can’t use them.',
    '[dry] Tip up, sides braced… [thump] Twenty years, and my foot is still the loudest part.',
    '[focused] Middle high, gap open… [sniffs] …Rabbit hardware. Your stamp stands.',
    '[wry] I ran it past three mirrors and a cat. No luck. Page verified. That part worked.',
    '[thoughtful] Funny. I know exactly where everything goes, and I still can’t do it. That’s why a human has to check it.',
    '[satisfied] Another page verified by a human. That’s the only kind of check that counts.',
  ],
};

/** Opening the lab after the first round of notes: a second look, in a new order. */
export const SECOND_ROUND: Readonly<Record<AgeBand, string>> = {
  younger: '[excited] Every page has your stamp! Let’s mix them up and look again.',
  older:
    '[intrigued] Every page verified. Turns out humans have two ways to make an r. My notes only had one. Round two.',
};
